import { useEffect, useState } from 'react';
import { projectsApi } from '@/api/projects.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { useProjectList } from '@/modules/projects/shared/useProjectList';
import type { BoardView, ProjectBoard } from '@/types/projects';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function ProjectsBoardsPage() {
  const { projects } = useProjectList();
  const [projectId, setProjectId] = useState<number | undefined>();
  const [boards, setBoards] = useState<ProjectBoard[]>([]);
  const [view, setView] = useState<BoardView | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ boardCode: '', boardName: '' });
  const [dragTaskId, setDragTaskId] = useState<number | null>(null);

  useEffect(() => {
    if (!projectId) { setBoards([]); setView(null); return; }
    projectsApi.listBoards(projectId).then(setBoards).catch((e) => showApiError(e, 'Failed to load boards'));
  }, [projectId]);

  const loadView = (boardId: number) => {
    projectsApi.getBoardView(boardId).then(setView).catch((e) => showApiError(e, 'Failed to load board'));
  };

  const createBoard = async () => {
    if (!projectId) return;
    try {
      await projectsApi.createBoard({ projectId, ...form });
      notify.success('Board created');
      setOpen(false);
      const list = await projectsApi.listBoards(projectId);
      setBoards(list);
      if (list[0]) loadView(list[0].id);
    } catch (err) {
      showApiError(err, 'Create board failed');
    }
  };

  const syncBoard = async () => {
    if (!view || !projectId) return;
    try {
      const added = await projectsApi.syncBoard(view.boardId, projectId);
      notify.success(`Synced ${added} tasks to board`);
      loadView(view.boardId);
    } catch (err) {
      showApiError(err, 'Sync failed');
    }
  };

  const moveCard = async (boardId: number, taskId: number, targetColumnId: number, sortIndex?: number) => {
    try {
      await projectsApi.moveBoardCard({ boardId, taskId, targetColumnId, sortIndex });
      loadView(boardId);
    } catch (err) {
      showApiError(err, 'Move failed');
    }
  };

  const onDropColumn = (columnId: number) => {
    if (!view || dragTaskId == null) return;
    void moveCard(view.boardId, dragTaskId, columnId);
    setDragTaskId(null);
  };

  return (
    <div>
      <PageHeader title="Boards" description="Kanban boards with drag-and-drop, WIP limits, and workflow columns"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Projects', to: '/projects' }, { label: 'Boards' }]}
        actions={<>
          {view && <Button variant="secondary" onClick={() => void syncBoard()}>Sync tasks</Button>}
          <Button onClick={() => setOpen(true)} disabled={!projectId}>New board</Button>
        </>} />
      <div className="mb-4 flex gap-3 flex-wrap">
        <Select
          placeholder="Select project"
          value={String(projectId ?? '')}
          onChange={(v) => setProjectId(v ? Number(v) : undefined)}
          options={projects.map((p) => ({ value: String(p.id), label: p.projectName }))}
        />
        {boards.map((b) => (
          <Button key={b.id} variant="secondary" onClick={() => loadView(b.id)}>{b.boardName}</Button>
        ))}
      </div>
      {view && (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {view.columns.map((col) => (
            <div
              key={col.columnId}
              className="min-w-[260px] flex-shrink-0 card p-3 bg-slate-50 dark:bg-slate-900/40"
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDropColumn(col.columnId)}
            >
              <h3 className="font-semibold text-sm mb-3">{col.columnName} ({col.cards.length}{col.wipLimit ? ` / ${col.wipLimit}` : ''})</h3>
              <ul className="space-y-2 min-h-[80px]">
                {col.cards.map((c) => (
                  <li
                    key={c.cardId}
                    draggable
                    onDragStart={() => setDragTaskId(c.taskId)}
                    className="rounded border bg-white dark:bg-slate-800 p-2 text-sm cursor-grab active:cursor-grabbing shadow-sm"
                  >
                    <div className="font-medium">{c.taskTitle ?? `Task #${c.taskId}`}</div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title="Create board" footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={() => void createBoard()}>Save</Button></>}>
        <div className="space-y-3">
          <Input label="Board code" value={form.boardCode} onChange={(e) => setForm({ ...form, boardCode: e.target.value })} />
          <Input label="Board name" value={form.boardName} onChange={(e) => setForm({ ...form, boardName: e.target.value })} />
        </div>
      </Modal>
    </div>
  );
}
